import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, getIdTokenResult } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFirebaseConfig } from "../config/firebaseConfig";

const app = initializeApp(getFirebaseConfig());
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/**
 * Registers a new user with campus and role, saving to Firebase Auth and Firestore.
 * @param {string} email
 * @param {string} password
 * @param {string} campus
 * @param {string} role
 * @returns {Promise<object>} The created user profile
 */
export async function registerUser(email, password, campus, role) {
  // Create user in Firebase Auth
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Save user profile in Firestore
  const userProfile = {
    uid: user.uid,
    email: user.email,
    campus,
    role,
    createdAt: new Date().toISOString(),
  };
  await setDoc(doc(db, "users", user.uid), userProfile);
  return userProfile;
}

/**
 * Logs in a user and fetches their profile from Firestore.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} The user profile from Firestore
 */
export async function loginUser(email, password) {
  // Login with Firebase Auth
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Fetch user profile from Firestore
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data() || {};
      return {
        uid: user.uid,
        email: user.email,
        ...data
      };
    }
  } catch (e) {
    // ignore and try token claims fallback
  }
  // Fallback to custom claims if users/{uid} profile is missing or unreadable
  const token = await getIdTokenResult(user);
  const claims = token?.claims || {};
  const roles = claims.roles || [];
  const role = Array.isArray(roles) && roles.length ? roles[0] : (claims.superadmin ? "superadmin" : (claims.admin ? "admin" : "readonly"));
  return {
    uid: user.uid,
    email: user.email,
    campus: claims.campus || "all",
    role,
    roles: Array.isArray(roles) ? roles : (role ? [role] : []),
    loadedFrom: "claims"
  };
}
